// Love Hotel hideout marking (world.json safehouses id "hotel"): a curtained drive-in entrance in the wall above the
// rect, a pink heart neon, a REST / STAY price board, and a pink outline plus light pool on the ground.
// Cosmetic only. Painted from hd-scene.ts after the buildings and lights, in scene hd px.
import { HT, K, type G, px, frame, dk, lt, alpha, text, textW } from './hd-kit';

/** x, y, w, h: the safehouse rect in scene hd px (top-left of its tiles). */
export function paintHotelMark(g: G, x: number, y: number, w: number, h: number) {
  // ground: light pool and a dashed outline over the rect
  g.save();
  g.globalCompositeOperation = 'lighter';
  const cx = x + w / 2, cy = y + h / 2 - 4;
  g.translate(cx, cy); g.scale(1, 0.5);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, w * 0.62);
  grd.addColorStop(0, alpha(K.pink, 0.34)); grd.addColorStop(1, alpha(K.pink, 0));
  g.fillStyle = grd; g.fillRect(-w, -w, w * 2, w * 2);
  g.restore();
  g.save(); g.lineWidth = 2; g.setLineDash([8, 6]); g.strokeStyle = alpha(K.pink, 0.7);
  g.strokeRect(x + 3, y + 3, w - 6, h - 6); g.restore();
  // pavement chevrons pointing into the curtain
  for (let i = 0; i < 3; i++) {
    const px0 = x + 40 + i * 16, py0 = y + 36 - i * 0;
    px(g, px0, py0, 8, 2, alpha(K.pink, 0.55)); px(g, px0 + 2, py0 - 2, 4, 2, alpha(K.pink, 0.55)); px(g, px0 + 4, py0 - 4, 0, 0, K.pink);
  }

  // entrance: a door frame in the wall (the wall's lowest rows sit just above y), vinyl curtain strips
  const fx = x + 14, fw = 64, fy = y - 54, fh = 54;
  px(g, fx - 3, fy - 3, fw + 6, fh + 3, K.ink);
  px(g, fx, fy, fw, fh, dk(K.pink, 0.8));
  const strip = 8;
  for (let i = 0; i < fw / strip; i++) {
    const sx = fx + i * strip, c = i % 2 ? dk(K.pink, 0.55) : dk(K.pink, 0.4);
    px(g, sx, fy + 4, strip - 1, fh - 4, c);
    px(g, sx, fy + 4, 1, fh - 4, alpha(K.white, 0.12)); // sheen
    px(g, sx + 2, fy + fh - 6 - (i % 3) * 3, strip - 4, 2, alpha(K.pink, 0.4)); // swaying hem
  }
  px(g, fx, fy, fw, 4, K.slate); px(g, fx, fy + 4, fw, 1, K.ink); // curtain rail
  // dark gap in the middle: the way in
  px(g, fx + fw / 2 - 1, fy + 5, 2, fh - 5, K.ink);

  // heart neon above the entrance
  const hx = fx + fw / 2 - 9, hy = fy - 24;
  const heart = ['.XX..XX.', 'XXXXXXXX', 'XXXXXXXX', '.XXXXXX.', '..XXXX..', '...XX...'];
  const hs = 3;
  heart.forEach((row, j) => [...row].forEach((c, i) => {
    if (c === 'X') px(g, hx - 3 + i * hs, hy + j * hs, hs, hs, lt(K.pink, 0.15));
  }));
  g.save(); g.globalCompositeOperation = 'lighter';
  const hg = g.createRadialGradient(hx + 9, hy + 8, 0, hx + 9, hy + 8, 44);
  hg.addColorStop(0, alpha(K.pink, 0.5)); hg.addColorStop(1, alpha(K.pink, 0));
  g.fillStyle = hg; g.fillRect(hx - 40, hy - 40, 100, 100); g.restore();

  // price board beside the door
  const bx = x + 88, bw = 36, by = y - 50, bh = 38;
  px(g, bx - 1, by - 1, bw + 2, bh + 2, K.ink); px(g, bx, by, bw, bh, K.night);
  frame(g, bx + 1, by + 1, bw - 2, bh - 2, alpha(K.pink, 0.8));
  const line = (s: string, j: number, c: string) => text(g, s, bx + Math.round((bw - textW(s)) / 2), by + 5 + j * 10, c);
  line('REST', 0, K.pink); line('STAY', 1, K.amber); line('24H', 2, K.cyan);
  px(g, bx + 3, by + 14, bw - 6, 1, alpha(K.pink, 0.5)); px(g, bx + 3, by + 24, bw - 6, 1, alpha(K.pink, 0.5));
  void HT;
}
